import classnames from 'classnames';
import { graphql, PageProps } from 'gatsby';
import React, { FC, useCallback, useMemo, useState } from 'react';

import { CITIES } from '../../constants/contacts';
import { usePathPrefix } from '../../constants/hooks';
import { t } from '../../i18n';
import { IHeaderQuery } from '../../queries/headers/types';

import ContactsCard from '../../components/contacts/ContactsCard';
import ContactsMap from '../../components/contacts/ContactsMap';
import { BuyButton } from '../../components/main/BuyButton';
import { YellowHeader } from '../../components/main/YellowHeader';
import { MainLayout } from '../../templates/MainLayout';

import styles from './styles.module.scss';

type IProps = PageProps<IHeaderQuery, { locale: string }>;

const ContactsPage: FC<IProps> = ({ data, pageContext: { locale = 'ru' } }) => {
  const prefix = usePathPrefix();
  const [tab, setTab] = useState<typeof CITIES[keyof typeof CITIES]>(
    CITIES.MOSCOW
  );
  const setTabMoscow = useCallback(() => setTab(CITIES.MOSCOW), [setTab]);
  const setTabNovosibirsk = useCallback(
    () => setTab(CITIES.NOVOSIBIRSK),
    [setTab]
  );

  const header = useMemo(
    () => data.headers.edges.find((edge) => edge.node),
    [data.headers, locale]
  );

  return (
    <MainLayout
      locale={locale}
      title="seo.pages.contacts.title"
      description="seo.pages.contacts.description"
    >
      <YellowHeader header={t('contacts.title')}>
        {header?.node?.html || ''}
      </YellowHeader>

      <div className={classnames('content', styles.wrap)}>
        <ContactsMap tab={tab} setTab={setTab} />

        <div className={styles.grid}>
          <ContactsCard
            icon={`${prefix}/contacts/moscow.svg`}
            title={t('contacts.moscow')}
            iconAlt="star"
            selected={tab === CITIES.MOSCOW}
            onClick={setTabMoscow}
          >
            <address>{header?.node.frontmatter.moscow_address}</address>
          </ContactsCard>

          <ContactsCard
            icon={`${prefix}/contacts/novosibirsk.svg`}
            title={t('contacts.novosibirsk')}
            iconAlt="snowflake"
            selected={tab === CITIES.NOVOSIBIRSK}
            onClick={setTabNovosibirsk}
          >
            <address>{header?.node.frontmatter.novosibirsk_address}</address>
          </ContactsCard>

          <ContactsCard
            icon={`${prefix}/contacts/phone.svg`}
            title={
              <a
                href={`tel://${header?.node.frontmatter.moscow_phone.replace(
                  /[()\s]/g,
                  ''
                )}`}
              >
                {header?.node.frontmatter.moscow_phone}
              </a>
            }
            iconAlt="phone"
          >
            <div className={styles.address}>{t('contacts.phone_moscow')}</div>
          </ContactsCard>

          <ContactsCard
            icon={`${prefix}/contacts/phone.svg`}
            title={
              <a
                href={`tel://${header?.node.frontmatter.novosibirsk_phone.replace(
                  /[()\s]/g,
                  ''
                )}`}
              >
                {header?.node.frontmatter.novosibirsk_phone}
              </a>
            }
            iconAlt="phone"
          >
            <div className={styles.address}>
              {t('contacts.phone_novosibirsk')}
            </div>
          </ContactsCard>

          <ContactsCard
            icon={`${prefix}/contacts/email.svg`}
            title={
              <a href={`mailto:${header?.node.frontmatter.email}`}>
                {header?.node.frontmatter.email || 'header.email'}
              </a>
            }
            iconAlt="email"
          >
            <span>{t('contacts.email')}</span>
          </ContactsCard>
        </div>

        <div className={styles.button}>
          <BuyButton squared>{t('contacts.button')}</BuyButton>
        </div>

        <div className={styles.legal_contacts_container}>
          <div className={styles.legal_contacts_container__info}>
            <span className={styles.legal_contacts_container__subheading}>
              Сведения об организации в соответствии с Приказом Минцифры России
              от 02.06.2025 № 511
            </span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Полное наименование:
            </span>
            <span>Общество с ограниченной ответственностью «АЙСРОК ЛАБ»</span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              ИНН:
            </span>
            <span>5406843466</span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Юридический адрес:
            </span>
            <span>
              630099, НОВОСИБИРСКАЯ ОБЛАСТЬ, Г.О. ГОРОД НОВОСИБИРСК, Г
              НОВОСИБИРСК, УЛ ЯДРИНЦЕВСКАЯ, ЗД. 68/1
            </span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Основной ОКВЭД:
            </span>
            <span>
              62.01 (Разработка компьютерного программного обеспечения)
            </span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Дополнительные ОКВЭД:
            </span>
            <span>62.02, 62.09, 63.11, 63.11.1, 95.11</span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Email:
            </span>
            <span className={styles.legal_contacts_container__underline}>
              info@icerockdev.com
            </span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Телефон:
            </span>
            <span className={styles.legal_contacts_container__underline}>
              +7 (383) 373 29 19
            </span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Среда разработки:
            </span>
            <ul>
              <li>xcode;</li>
              <li>android studio.</li>
            </ul>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Языки:
            </span>
            <ul>
              <li>Kotlin;</li>
              <li>Java;</li>
              <li>Yaml/Json pipeline;</li>
              <li>Sql для SQLLite;</li>
              <li>SqlDelight;</li>
              <li>Room;</li>
              <li>Bash/Shell;</li>
              <li>CI;</li>
              <li>Pipeline.</li>
            </ul>
          </div>

          <div>
            <span className={styles.legal_contacts_container__subheading}>
              Стоимость услуг:
            </span>
            <span>
              Почасовая оплата. Стоимость 1 часа работы специалиста — от 4 500
              руб./час. Окончательная стоимость является договорной,
              определяется в зависимости от сложности и объёма работ.
            </span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__info}>
              ООО «АЙСРОК ЛАБ» является аккредитованной ИТ-компанией. Код вида
              деятельности в области информационных технологий по Приказу
              Минцифры России от 11.05.2023 № 449: 1.01.
            </span>
          </div>

          <div>
            <span className={styles.legal_contacts_container__italic}>
              Сведения актуальны по состоянию на: 27.05.2026
            </span>
          </div>
        </div>
      </div>
    </MainLayout>
  );
};

export const query = graphql`
  query {
    headers: allMarkdownRemark(
      filter: { fileAbsolutePath: { regex: "/contacts/header/header/" } }
    ) {
      ...HeaderFields
    }
  }
`;

export default ContactsPage;
